import { useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, View } from 'react-native';
import { Badge } from '../../../components/primitives/Badge';
import { Button } from '../../../components/primitives/Button';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Body, Caption, Heading4 } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import type { ExperimentReport, VoiceAgentExperiment } from '../experiments-api';
import { useVoiceAgentVersions } from '../hooks';
import {
  useExperimentReport,
  useExperiments,
  useUpdateExperimentStatus,
} from '../hooks/useExperiments';
import { CreateExperimentModal } from './CreateExperimentModal';

interface ExperimentsPanelProps {
  agentId: string;
  organizationId: string;
}

const STATUS_BADGE: Record<VoiceAgentExperiment['status'], 'amber' | 'green' | 'neutral' | 'blue'> =
  {
    draft: 'amber',
    running: 'green',
    paused: 'neutral',
    concluded: 'blue',
  };

export function ExperimentsPanel({ agentId, organizationId }: ExperimentsPanelProps) {
  const { t } = useTranslation();
  const { data: experiments = [], isLoading } = useExperiments(agentId, organizationId);
  const { data: versions = [] } = useVoiceAgentVersions(agentId, organizationId);
  const updateStatus = useUpdateExperimentStatus(agentId);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const createModal = (
    <CreateExperimentModal
      agentId={agentId}
      organizationId={organizationId}
      versions={versions}
      visible={isCreateOpen}
      onClose={() => setIsCreateOpen(false)}
    />
  );

  if (isLoading) {
    return (
      <View
        style={styles.center}
        accessible
        accessibilityLabel={t('voiceAgents.experiments.loading')}
      >
        <ActivityIndicator color={colors.primary.main} />
      </View>
    );
  }

  if (experiments.length === 0) {
    return (
      <View
        style={styles.center}
        accessible
        accessibilityLabel={t('voiceAgents.experiments.emptyLabel')}
      >
        <Body style={styles.bodyMuted}>{t('voiceAgents.experiments.emptyTitle')}</Body>
        <Caption style={styles.bodyMuted}>{t('voiceAgents.experiments.emptyHint')}</Caption>
        <Button
          variant="primary"
          size="sm"
          onPress={() => setIsCreateOpen(true)}
          style={styles.newExperimentButton}
          accessibilityLabel={t('voiceAgents.experiments.newExperimentA11y')}
        >
          {t('voiceAgents.experiments.newExperiment')}
        </Button>
        {createModal}
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <HStack style={styles.header}>
        <Button
          variant="outline"
          size="sm"
          onPress={() => setIsCreateOpen(true)}
          accessibilityLabel={t('voiceAgents.experiments.newExperimentA11y')}
        >
          {t('voiceAgents.experiments.newExperiment')}
        </Button>
      </HStack>
      <ScrollView
        contentContainerStyle={styles.list}
        accessibilityLabel={t('voiceAgents.experiments.listLabel')}
      >
        {experiments.map((exp) => (
          <ExperimentCard
            key={exp.id}
            experiment={exp}
            organizationId={organizationId}
            onStart={() =>
              updateStatus.mutate({
                experimentId: exp.id,
                dto: { organizationId, status: 'running' },
              })
            }
            onPause={() =>
              updateStatus.mutate({
                experimentId: exp.id,
                dto: { organizationId, status: 'paused' },
              })
            }
            isUpdating={updateStatus.isPending}
          />
        ))}
      </ScrollView>
      {createModal}
    </View>
  );
}

interface ExperimentCardProps {
  experiment: VoiceAgentExperiment;
  organizationId: string;
  onStart: () => void;
  onPause: () => void;
  isUpdating: boolean;
}

function ExperimentCard({
  experiment,
  organizationId,
  onStart,
  onPause,
  isUpdating,
}: ExperimentCardProps) {
  const { t } = useTranslation();
  const { data: report } = useExperimentReport(
    experiment.voiceAgentId,
    experiment.id,
    organizationId,
  );

  const statusKey = `voiceAgents.experiments.status.${experiment.status}`;
  const statusResolved = t(statusKey);
  const statusLabel = statusResolved === statusKey ? experiment.status : statusResolved;

  return (
    <View
      style={styles.card}
      accessible
      accessibilityLabel={`${t('voiceAgents.experiments.experimentPrefix')} ${experiment.name}, ${t('voiceAgents.experiments.statusPrefix')} ${statusLabel}`}
    >
      <HStack style={styles.cardHeader}>
        <VStack spacing="xs" style={styles.cardHeaderText}>
          <Heading4 accessibilityRole="header">{experiment.name}</Heading4>
          {experiment.description ? (
            <Caption style={styles.bodyMuted}>{experiment.description}</Caption>
          ) : null}
        </VStack>
        <Badge
          color={STATUS_BADGE[experiment.status]}
          accessibilityLabel={`${t('voiceAgents.experiments.statusPrefix')}: ${statusLabel}`}
        >
          {statusLabel}
        </Badge>
      </HStack>

      <HStack style={styles.metaRow}>
        <Caption style={styles.bodyMuted}>
          {t('voiceAgents.experiments.split')} {experiment.splitPercent}/
          {100 - experiment.splitPercent}
        </Caption>
        <Caption style={styles.bodyMuted}>
          {t('voiceAgents.experiments.target')}: {experiment.outcomeTarget}
        </Caption>
      </HStack>

      {report ? <ReportRows report={report} /> : null}

      <HStack spacing="sm" style={styles.actions}>
        {experiment.status === 'running' ? (
          <Button
            variant="outline"
            size="sm"
            onPress={onPause}
            disabled={isUpdating}
            accessibilityLabel={t('voiceAgents.experiments.pauseA11y')}
          >
            {t('voiceAgents.experiments.pause')}
          </Button>
        ) : null}
        {(experiment.status === 'draft' || experiment.status === 'paused') && (
          <Button
            variant="primary"
            size="sm"
            onPress={onStart}
            disabled={isUpdating}
            accessibilityLabel={t('voiceAgents.experiments.startA11y')}
          >
            {experiment.status === 'draft'
              ? t('voiceAgents.experiments.start')
              : t('voiceAgents.experiments.resume')}
          </Button>
        )}
      </HStack>
    </View>
  );
}

function ReportRows({ report }: { report: ExperimentReport }) {
  const { t } = useTranslation();
  const { variantA, variantB, liftRelative, pValue, significantAt95, insufficientSample } =
    report.report;

  return (
    <VStack spacing="xs" style={styles.report}>
      <ReportRow label="A" total={variantA.totalCalls} rate={variantA.conversionRate} />
      <ReportRow label="B" total={variantB.totalCalls} rate={variantB.conversionRate} />
      <HStack style={styles.statsRow}>
        <Caption style={styles.bodyMuted}>
          {t('voiceAgents.experiments.lift')}:{' '}
          {Number.isFinite(liftRelative) ? `${(liftRelative * 100).toFixed(1)}%` : 'n/a'}
        </Caption>
        <Caption style={styles.bodyMuted}>
          {t('voiceAgents.experiments.pValue')}: {pValue !== null ? pValue.toFixed(3) : 'n/a'}
        </Caption>
        <Caption
          style={significantAt95 ? styles.significantBadge : styles.bodyMuted}
          accessibilityLabel={
            significantAt95
              ? t('voiceAgents.experiments.significantA11y')
              : t('voiceAgents.experiments.notSignificantA11y')
          }
        >
          {significantAt95
            ? t('voiceAgents.experiments.significant')
            : t('voiceAgents.experiments.notSignificant')}
        </Caption>
      </HStack>
      {insufficientSample ? (
        <Caption style={styles.warn} accessibilityLiveRegion="polite">
          {t('voiceAgents.experiments.insufficientSample')}
        </Caption>
      ) : null}
    </VStack>
  );
}

function ReportRow({ label, total, rate }: { label: string; total: number; rate: number }) {
  const { t } = useTranslation();
  return (
    <HStack style={styles.reportRow}>
      <Caption style={styles.label}>
        {t('voiceAgents.experiments.variant')} {label}
      </Caption>
      <Caption style={styles.bodyMuted}>
        {total} {t('voiceAgents.experiments.calls')}
      </Caption>
      <Caption style={styles.bodyMuted}>
        {(rate * 100).toFixed(1)}% {t('voiceAgents.experiments.conv')}
      </Caption>
    </HStack>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  header: {
    padding: 16,
    paddingBottom: 0,
    justifyContent: 'flex-end',
  },
  newExperimentButton: {
    marginTop: 12,
  },
  list: {
    padding: 16,
    gap: 12,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 6,
  },
  card: {
    padding: 16,
    borderRadius: 12,
    backgroundColor: colors.background.secondary,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    gap: 12,
  },
  cardHeader: {
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardHeaderText: {
    flex: 1,
    marginRight: 12,
  },
  metaRow: {
    gap: 16,
  },
  report: {
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: colors.border.subtle,
  },
  reportRow: {
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statsRow: {
    justifyContent: 'space-between',
    marginTop: 4,
  },
  actions: {
    justifyContent: 'flex-end',
  },
  label: {
    color: colors.text.primary,
    fontWeight: '600',
  },
  bodyMuted: {
    color: colors.text.tertiary,
  },
  significantBadge: {
    color: colors.primary.main,
    fontWeight: '600',
  },
  warn: {
    color: colors.status.warning.main,
    marginTop: 4,
  },
});
